// OrbCanvas.js — Crystal ball renderer (Three.js)
//
// Artistic compositing approach — NOT physically-based glass.
// The designer's spec describes a 9-layer stack. We implement it as:
//
//   Interior layers (rendered directly, bright and visible):
//     1. Base glow sphere — self-luminous purple core
//     2–4. Three swirl planes — rotating nebula textures (additive)
//     5. Glitter particles — fading points of light (additive)
//
//   Glass surface layers (shader overlays on top):
//     6. Limb darkening sphere — Fresnel edge fade + depth vignette
//     7–8. Specular highlight sphere — two highlights + subtle rim glow
//     9. Surface tint — baked into the limb shader
//
// The canvas renders with alpha:true. An HTML text layer sits behind
// this canvas (CSS z-index 1 vs 2), so the glass surface highlights
// appear ON TOP of the daemon's text, creating the crystal ball illusion.

import * as THREE from 'three';
import { DEFAULT_MATERIAL } from './materials.js';

// ── State parameter table ────────────────────────────────────────────────────
const STATE_PARAMS = {
  nascent:   { swirlSpeeds: [21, 30, 40], swirlOpacity: 0.28, particleCount: 11, glowIntensity: 0.35, breathScale: 1.032, brightness: 0.85 },
  stirring:  { swirlSpeeds: [18, 24, 34], swirlOpacity: 0.38, particleCount: 15, glowIntensity: 0.50, breathScale: 1.036, brightness: 0.95 },
  awakening: { swirlSpeeds: [16, 22, 30], swirlOpacity: 0.48, particleCount: 17, glowIntensity: 0.65, breathScale: 1.038, brightness: 1.05 },
  bound:     { swirlSpeeds: [15, 20, 28], swirlOpacity: 0.55, particleCount: 19, glowIntensity: 0.78, breathScale: 1.040, brightness: 1.10 },
  sovereign: { swirlSpeeds: [14, 18, 26], swirlOpacity: 0.65, particleCount: 20, glowIntensity: 0.90, breathScale: 1.040, brightness: 1.18 },
};

function lerp(a, b, t) { return a + (b - a) * t; }
function lerpParams(from, to, t) {
  return {
    swirlSpeeds: from.swirlSpeeds.map((s, i) => lerp(s, to.swirlSpeeds[i], t)),
    swirlOpacity: lerp(from.swirlOpacity, to.swirlOpacity, t),
    particleCount: Math.round(lerp(from.particleCount, to.particleCount, t)),
    glowIntensity: lerp(from.glowIntensity, to.glowIntensity, t),
    breathScale: lerp(from.breathScale, to.breathScale, t),
    brightness: lerp(from.brightness, to.brightness, t),
  };
}

function hexToVec3(hex) {
  if (!hex || hex[0] !== '#') return new THREE.Color(0x7040d0);
  return new THREE.Color(hex);
}

// ── Swirl texture — procedural nebula wisps ─────────────────────────────────
function createSwirlTexture(seed) {
  const size = 512;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, size, size);
  const cx = size / 2, cy = size / 2;

  // 4-6 elliptical wisps for richer nebula feel
  const count = 4 + (seed % 3);
  for (let i = 0; i < count; i++) {
    const angle = ((seed * 137.5 + i * 50) % 360) * Math.PI / 180;
    const dist = size * 0.08 + (((seed * 31 + i * 17) % 100) / 100) * size * 0.28;
    const wx = cx + Math.cos(angle) * dist;
    const wy = cy + Math.sin(angle) * dist;
    const rx = size * 0.18 + (((seed * 47 + i * 23) % 100) / 100) * size * 0.22;
    const ry = rx * (0.3 + (((seed * 71 + i * 13) % 100) / 100) * 0.4);

    const grad = ctx.createRadialGradient(wx, wy, 0, wx, wy, rx);
    const hue = 260 + ((seed * 11 + i * 25) % 45);
    const lightness = 65 + ((seed * 13 + i * 7) % 15);
    grad.addColorStop(0, `hsla(${hue}, 70%, ${lightness}%, 0.7)`);
    grad.addColorStop(0.3, `hsla(${hue}, 60%, ${lightness - 10}%, 0.35)`);
    grad.addColorStop(0.7, `hsla(${hue}, 50%, ${lightness - 20}%, 0.08)`);
    grad.addColorStop(1, 'hsla(270, 50%, 50%, 0)');

    ctx.save();
    ctx.translate(wx, wy);
    ctx.rotate(angle * 0.7);
    ctx.scale(1, ry / rx);
    ctx.translate(-wx, -wy);
    ctx.fillStyle = grad;
    ctx.fillRect(wx - rx * 2, wy - rx * 2, rx * 4, rx * 4);
    ctx.restore();
  }

  // Add a soft overall glow in the center
  const centerGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, size * 0.4);
  centerGrad.addColorStop(0, 'hsla(270, 60%, 60%, 0.15)');
  centerGrad.addColorStop(1, 'hsla(270, 60%, 40%, 0)');
  ctx.fillStyle = centerGrad;
  ctx.fillRect(0, 0, size, size);

  const tex = new THREE.CanvasTexture(canvas);
  tex.needsUpdate = true;
  return tex;
}

// ── Limb darkening shader (glass surface layer 1) ───────────────────────────
// Creates the "looking into a sphere" effect — transparent center, dark edges.
const limbVertexShader = `
  varying vec3 vNormal;
  varying vec3 vViewDir;
  void main() {
    vNormal = normalize(normalMatrix * normal);
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    vViewDir = normalize(-mv.xyz);
    gl_Position = projectionMatrix * mv;
  }
`;

const limbFragmentShader = `
  uniform vec3 uEdgeColor;
  uniform float uEdgePower;
  uniform float uEdgeOpacity;
  uniform vec3 uSurfaceTint;
  uniform float uSurfaceTintAlpha;

  varying vec3 vNormal;
  varying vec3 vViewDir;

  void main() {
    float NdotV = max(dot(vNormal, vViewDir), 0.0);
    float edge = pow(1.0 - NdotV, uEdgePower);

    // Smooth edge transition — compressed to avoid harsh cutoff
    edge = smoothstep(0.0, 1.0, edge);

    vec3 col = mix(uSurfaceTint, uEdgeColor, edge);
    float alpha = mix(uSurfaceTintAlpha, uEdgeOpacity, edge);

    gl_FragColor = vec4(col * alpha, alpha);
  }
`;

// ── Specular highlight shader (glass surface layer 2) ───────────────────────
// Additive blending — bright spots for the specular reflections + rim glow.
const specVertexShader = `
  varying vec3 vNormal;
  varying vec3 vViewDir;
  varying vec3 vWorldNormal;
  void main() {
    vNormal = normalize(normalMatrix * normal);
    vWorldNormal = normalize((modelMatrix * vec4(normal, 0.0)).xyz);
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    vViewDir = normalize(-mv.xyz);
    gl_Position = projectionMatrix * mv;
  }
`;

const specFragmentShader = `
  uniform vec3 uLight1Dir;
  uniform vec3 uLight2Dir;
  uniform vec3 uSpecColor;
  uniform float uSpec1Intensity;
  uniform float uSpec1Power;
  uniform float uSpec2Intensity;
  uniform float uSpec2Power;
  uniform vec3 uRimColor;
  uniform float uRimPower;
  uniform float uRimIntensity;

  varying vec3 vNormal;
  varying vec3 vViewDir;
  varying vec3 vWorldNormal;

  void main() {
    // Primary specular — upper-left, soft and bright
    vec3 H1 = normalize(uLight1Dir + vViewDir);
    float s1 = pow(max(dot(vNormal, H1), 0.0), uSpec1Power) * uSpec1Intensity;

    // Add a broader soft glow around the primary spec
    float s1soft = pow(max(dot(vNormal, H1), 0.0), uSpec1Power * 0.25) * uSpec1Intensity * 0.3;

    // Secondary specular — lower-right, dim
    vec3 H2 = normalize(uLight2Dir + vViewDir);
    float s2 = pow(max(dot(vNormal, H2), 0.0), uSpec2Power) * uSpec2Intensity;

    // Rim glow — subtle Fresnel edge light
    float rim = pow(1.0 - max(dot(vNormal, vViewDir), 0.0), uRimPower) * uRimIntensity;

    vec3 col = uSpecColor * (s1 + s1soft + s2) + uRimColor * rim;
    float alpha = s1 + s1soft + s2 + rim;

    gl_FragColor = vec4(col, clamp(alpha, 0.0, 1.0));
  }
`;

// ── OrbRenderer ──────────────────────────────────────────────────────────────
export class OrbRenderer {
  constructor(canvas) {
    this.canvas = canvas;
    this.disposed = false;

    // State
    this.currentState = 'nascent';
    this.targetState = 'nascent';
    this.stateBlend = 1;
    this.stateTransStart = 0;
    this.params = { ...STATE_PARAMS.nascent };

    this.material = DEFAULT_MATERIAL;
    this.matBlend = 0;

    // ── Three.js setup ───────────────────────────────────────────────────
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      alpha: true,
      antialias: true,
      premultipliedAlpha: true,
      powerPreference: 'high-performance',
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    // No tone mapping — we want direct artistic control over colors
    this.renderer.toneMapping = THREE.NoToneMapping;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(38, 1, 0.1, 100);
    this.camera.position.set(0, 0, 3.8);

    this.clock = new THREE.Clock();

    // ═══════════════════════════════════════════════════════════════════
    // INTERIOR LAYERS (rendered directly — bright and visible)
    // ═══════════════════════════════════════════════════════════════════

    // ── Layer 1: Base glow sphere (self-luminous core) ───────────────
    this.glowMat = new THREE.MeshBasicMaterial({
      color: new THREE.Color(0x7040d0),
      transparent: true,
      opacity: 0.40,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    this.glowMesh = new THREE.Mesh(
      new THREE.SphereGeometry(0.92, 48, 48),
      this.glowMat,
    );
    this.glowMesh.renderOrder = 0;
    this.scene.add(this.glowMesh);

    // ── Layer 1b: Deep core — brighter, smaller inner glow ───────────
    this.coreMat = new THREE.MeshBasicMaterial({
      color: new THREE.Color(0x9060e0),
      transparent: true,
      opacity: 0.30,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    this.coreMesh = new THREE.Mesh(
      new THREE.SphereGeometry(0.55, 32, 32),
      this.coreMat,
    );
    this.coreMesh.renderOrder = 0;
    this.scene.add(this.coreMesh);

    // ── Layers 2–4: Swirl planes (rotating nebula textures) ──────────
    this.swirls = [];
    for (let i = 0; i < 3; i++) {
      const tex = createSwirlTexture(i * 7 + 3);
      const mat = new THREE.MeshBasicMaterial({
        map: tex,
        transparent: true,
        opacity: 0.35,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      });
      const mesh = new THREE.Mesh(new THREE.PlaneGeometry(1.7, 1.7), mat);
      mesh.rotation.x = (i * 0.75) - 0.4;
      mesh.rotation.y = i * 1.1;
      mesh.renderOrder = 1;
      this.scene.add(mesh);
      this.swirls.push({ mesh, mat, tex, dir: i % 2 === 0 ? 1 : -1 });
    }

    // ── Layer 5: Particle system (glitter) ───────────────────────────
    this.maxParticles = 24;
    const positions = new Float32Array(this.maxParticles * 3);
    const lifetimes = new Float32Array(this.maxParticles);
    const durations = new Float32Array(this.maxParticles);
    const sizes     = new Float32Array(this.maxParticles);

    for (let i = 0; i < this.maxParticles; i++) {
      this._spawnParticle(positions, lifetimes, durations, sizes, i);
    }

    this.particleGeo = new THREE.BufferGeometry();
    this.particleGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this.particleGeo.setAttribute('lifetime', new THREE.BufferAttribute(lifetimes, 1));
    this.particleGeo.setAttribute('duration', new THREE.BufferAttribute(durations, 1));
    this.particleGeo.setAttribute('size',     new THREE.BufferAttribute(sizes, 1));

    this.particleMat = new THREE.ShaderMaterial({
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      uniforms: {
        uTime: { value: 0 },
        uColor: { value: new THREE.Color(0xc8a0ff) },
        uPixelRatio: { value: this.renderer.getPixelRatio() },
      },
      vertexShader: `
        attribute float lifetime;
        attribute float duration;
        attribute float size;
        uniform float uTime;
        uniform float uPixelRatio;
        varying float vAlpha;
        void main() {
          float cycle = mod(uTime / duration + lifetime, 1.0);
          vAlpha = sin(cycle * 3.14159) * 0.9;
          float sc = 0.3 + sin(cycle * 3.14159) * 0.7;
          vec4 mvPos = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = size * sc * uPixelRatio * (200.0 / -mvPos.z);
          gl_Position = projectionMatrix * mvPos;
        }
      `,
      fragmentShader: `
        uniform vec3 uColor;
        varying float vAlpha;
        void main() {
          float d = length(gl_PointCoord - 0.5);
          if (d > 0.5) discard;
          float glow = smoothstep(0.5, 0.05, d);
          gl_FragColor = vec4(uColor * glow, vAlpha * glow);
        }
      `,
    });
    this.particles = new THREE.Points(this.particleGeo, this.particleMat);
    this.particles.renderOrder = 2;
    this.scene.add(this.particles);

    // ═══════════════════════════════════════════════════════════════════
    // GLASS SURFACE LAYERS (artistic shader overlays)
    // ═══════════════════════════════════════════════════════════════════

    const glassSphereGeo = new THREE.SphereGeometry(1.0, 64, 64);

    // ── Layer 6: Limb darkening + depth vignette + surface tint ──────
    this.limbMat = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      depthTest: false,
      vertexShader: limbVertexShader,
      fragmentShader: limbFragmentShader,
      uniforms: {
        uEdgeColor:       { value: new THREE.Color(0x0a0518) },
        uEdgePower:       { value: 2.8 },
        uEdgeOpacity:     { value: 0.75 },
        uSurfaceTint:     { value: new THREE.Color(0xc8a0ff) },
        uSurfaceTintAlpha: { value: 0.04 },
      },
    });
    this.limbMesh = new THREE.Mesh(glassSphereGeo, this.limbMat);
    this.limbMesh.renderOrder = 10;
    this.scene.add(this.limbMesh);

    // ── Layers 7–9: Specular highlights + rim glow ───────────────────
    this.specMat = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      depthTest: false,
      blending: THREE.AdditiveBlending,
      vertexShader: specVertexShader,
      fragmentShader: specFragmentShader,
      uniforms: {
        // Primary highlight — upper-left (as per designer spec)
        uLight1Dir:     { value: new THREE.Vector3(-0.5, 0.7, 0.6).normalize() },
        uSpec1Intensity: { value: 0.55 },
        uSpec1Power:     { value: 80.0 },
        // Secondary highlight — lower-right
        uLight2Dir:     { value: new THREE.Vector3(0.5, -0.35, 0.5).normalize() },
        uSpec2Intensity: { value: 0.12 },
        uSpec2Power:     { value: 40.0 },
        // Specular color
        uSpecColor:     { value: new THREE.Color(0xdcc8ff) },
        // Fresnel rim glow
        uRimColor:      { value: new THREE.Color(0x5030a0) },
        uRimPower:      { value: 4.5 },
        uRimIntensity:  { value: 0.18 },
      },
    });
    this.specMesh = new THREE.Mesh(
      new THREE.SphereGeometry(1.002, 64, 64),
      this.specMat,
    );
    this.specMesh.renderOrder = 11;
    this.scene.add(this.specMesh);
  }

  _spawnParticle(positions, lifetimes, durations, sizes, i) {
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(2 * Math.random() - 1);
    const r = 0.78 * Math.cbrt(Math.random());
    positions[i * 3]     = r * Math.sin(phi) * Math.cos(theta);
    positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
    positions[i * 3 + 2] = r * Math.cos(phi);
    lifetimes[i] = Math.random();
    durations[i] = 2.5 + Math.random() * 2.0;
    sizes[i] = 1.8 + Math.random() * 2.5;
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  resize(diam) {
    this.renderer.setSize(diam, diam);
  }

  setOrbState(name) {
    if (name === this.targetState) return;
    this.currentState = this.targetState;
    this.targetState = name;
    this.stateBlend = 0;
    this.stateTransStart = this.clock.getElapsedTime();
  }

  setMaterial(mat) {
    this.material = mat;
  }

  setMaterialBlend(t) {
    this.matBlend = Math.max(0, Math.min(1, t));
  }

  // ── Update (called from rAF loop) ─────────────────────────────────────────

  update(ts, dt) {
    if (this.disposed) return;
    const elapsed = this.clock.getElapsedTime();

    // ── State interpolation (3s transitions) ──────────────────────────
    if (this.stateBlend < 1) {
      this.stateBlend = Math.min((elapsed - this.stateTransStart) / 3.0, 1);
      const from = STATE_PARAMS[this.currentState] || STATE_PARAMS.nascent;
      const to = STATE_PARAMS[this.targetState] || STATE_PARAMS.nascent;
      this.params = lerpParams(from, to, this.stateBlend);
      if (this.stateBlend >= 1) this.currentState = this.targetState;
    } else {
      this.params = STATE_PARAMS[this.currentState] || STATE_PARAMS.nascent;
    }

    const p = this.params;
    const t = this.matBlend;
    const mat = this.material;

    // ── Material color blending ───────────────────────────────────────
    const baseGlow = new THREE.Color(0x7040d0);
    const matGlow = hexToVec3(mat.glow);
    const blendedGlow = baseGlow.clone().lerp(matGlow, t * 0.75);

    const baseCore = new THREE.Color(0x9060e0);
    const blendedCore = baseCore.clone().lerp(matGlow, t * 0.5);

    const basePrimary = new THREE.Color(0x2D1B69);
    const matPrimary = hexToVec3(mat.primary);

    // ── Inner glow ────────────────────────────────────────────────────
    this.glowMat.color.copy(blendedGlow);
    this.glowMat.opacity = 0.25 + p.glowIntensity * 0.25;

    this.coreMat.color.copy(blendedCore);
    this.coreMat.opacity = 0.15 + p.glowIntensity * 0.25;

    // ── Swirl rotation & opacity ──────────────────────────────────────
    this.swirls.forEach((s, i) => {
      const speed = p.swirlSpeeds[i];
      const angle = s.dir * (elapsed / speed) * Math.PI * 2;
      s.mesh.rotation.z = angle;
      s.mesh.rotation.x = Math.sin(elapsed * 0.12 + i * 1.5) * 0.35 + i * 0.75 - 0.4;
      s.mat.opacity = p.swirlOpacity;

      if (t > 0.1) {
        s.mat.color = new THREE.Color(0xffffff).lerp(matPrimary, t * 0.3);
      } else {
        s.mat.color = new THREE.Color(0xffffff);
      }
    });

    // ── Particles ─────────────────────────────────────────────────────
    this.particleMat.uniforms.uTime.value = elapsed;
    const particleColor = new THREE.Color(0xc8a0ff);
    if (t > 0.3) {
      const matParticle = hexToVec3(mat.particles);
      particleColor.lerp(matParticle, (t - 0.3) / 0.7);
    }
    this.particleMat.uniforms.uColor.value = particleColor;

    // Hide particles beyond the active count
    const posArr = this.particleGeo.attributes.position.array;
    const active = p.particleCount;
    for (let i = active; i < this.maxParticles; i++) {
      posArr[i * 3] = 0;
      posArr[i * 3 + 1] = 0;
      posArr[i * 3 + 2] = 0;
    }
    this.particleGeo.attributes.position.needsUpdate = true;

    // ── Glass surface — update for material blend and brightness ──────
    // Limb darkening edge color shifts toward material
    const limbEdge = new THREE.Color(0x0a0518);
    if (t > 0.2) {
      limbEdge.lerp(matPrimary.clone().multiplyScalar(0.15), (t - 0.2) / 0.8);
    }
    this.limbMat.uniforms.uEdgeColor.value.copy(limbEdge);

    // Rim glow picks up material color
    const rimColor = new THREE.Color(0x5030a0);
    if (t > 0.3) {
      rimColor.lerp(matGlow, (t - 0.3) / 0.7 * 0.5);
    }
    this.specMat.uniforms.uRimColor.value.copy(rimColor);

    // Brightness affects glow intensity and specular
    const brightnessFactor = p.brightness;
    this.specMat.uniforms.uSpec1Intensity.value = 0.55 * brightnessFactor;
    this.specMat.uniforms.uRimIntensity.value = 0.18 * brightnessFactor;

    // ── Render ────────────────────────────────────────────────────────
    this.renderer.render(this.scene, this.camera);
  }

  destroy() {
    this.disposed = true;
    this.glowMat.dispose();
    this.glowMesh.geometry.dispose();
    this.coreMat.dispose();
    this.coreMesh.geometry.dispose();
    this.limbMat.dispose();
    this.limbMesh.geometry.dispose();
    this.specMat.dispose();
    this.specMesh.geometry.dispose();
    this.particleGeo.dispose();
    this.particleMat.dispose();
    this.swirls.forEach(s => {
      s.mat.dispose();
      s.mesh.geometry.dispose();
      s.tex.dispose();
    });
    this.renderer.dispose();
  }
}
